import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, Upload, Trash2, Plus, Download } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

function todayStr() { return new Date().toISOString().slice(0, 10); }
function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; }
}
function isoWeekday(iso) {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short" }); } catch { return ""; }
}

export default function Holidays({ canEdit, lines }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState({ auto_sundays_off_by_year: {} });
  const [form, setForm] = useState({ date: todayStr(), name: "", working_override: false, line_ids: [] });
  const fileInputRef = useRef(null);
  const lineName = (id) => (lines || []).find((l) => l.id === id)?.name || id;

  const load = async () => {
    try {
      const [h, s] = await Promise.all([
        api.get("/holidays", { params: { year } }),
        api.get("/holidays/settings"),
      ]);
      setRows(h.data);
      setSettings({ auto_sundays_off_by_year: s.data?.auto_sundays_off_by_year || {} });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load holidays"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const autoSundayEnabled = useMemo(() => {
    const v = settings.auto_sundays_off_by_year?.[String(year)];
    return v === undefined ? true : !!v;
  }, [settings, year]);

  const toggleAutoSunday = async (val) => {
    if (!canEdit) return;
    try {
      await api.put("/holidays/settings", { year, enabled: !!val });
      toast.success(val ? `Sundays auto-marked off for ${year}` : `Sundays counted as working days for ${year}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const addOne = async () => {
    if (!canEdit) return;
    if (!form.date || !form.name.trim()) { toast.error("Date and name required"); return; }
    if (form.working_override && form.line_ids.length === 0) {
      toast.error("Select at least one line the working-day override applies to");
      return;
    }
    try {
      await api.post("/holidays", {
        date: form.date,
        name: form.name.trim(),
        working_override: !!form.working_override,
        applies_to: form.working_override ? form.line_ids : "all",
      });
      toast.success(`${form.working_override ? "Working-day override" : "Holiday"} added for ${fmt(form.date)}`);
      setForm({ date: todayStr(), name: "", working_override: false, line_ids: [] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const removeOne = async (id, date) => {
    if (!canEdit) return;
    if (!window.confirm(`Remove holiday on ${fmt(date)}?`)) return;
    try {
      await api.delete(`/holidays/${id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const parsed = XLSX.utils.sheet_to_json(ws, { raw: false, defval: "" });
      const holidays = parsed
        .map((r) => {
          const dateRaw = r.Date || r.date || r.DATE || r["Holiday Date"] || "";
          let date = "";
          if (dateRaw instanceof Date) {
            date = `${dateRaw.getFullYear()}-${String(dateRaw.getMonth() + 1).padStart(2, "0")}-${String(dateRaw.getDate()).padStart(2, "0")}`;
          } else if (typeof dateRaw === "string" && dateRaw.trim()) {
            // Accept both dd/mm/yyyy and yyyy-mm-dd
            const s = dateRaw.trim();
            if (/^\d{4}-\d{2}-\d{2}$/.test(s)) date = s;
            else {
              const parts = s.split(/[\/\-]/);
              if (parts.length === 3) {
                let [a, b, c] = parts;
                if (a.length === 4) date = `${a}-${b.padStart(2, "0")}-${c.padStart(2, "0")}`;
                else date = `${c.length === 2 ? "20" + c : c}-${b.padStart(2, "0")}-${a.padStart(2, "0")}`;
              }
            }
          }
          const name = String(r.Name || r.name || r.NAME || r["Holiday"] || r["Holiday Name"] || "").trim();
          return { date, name };
        })
        .filter((r) => r.date && r.name);
      if (!holidays.length) { toast.error("No valid rows found. Expected columns: Date, Name"); return; }
      const { data } = await api.post("/holidays/bulk", { rows: holidays });
      toast.success(`Bulk upload — added ${data.added}, updated ${data.updated}${data.skipped ? `, skipped ${data.skipped}` : ""}`);
      load();
    } catch (err) {
      toast.error("Failed to parse or upload — expected columns Date + Name");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ["Date", "Name"],
      [`${year}-01-01`, "New Year's Day"],
      [`${year}-08-15`, "Independence Day"],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Holidays");
    XLSX.writeFile(wb, `holidays-template-${year}.xlsx`);
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const r of rows) {
      const key = r.date.slice(0, 7); // YYYY-MM
      (g[key] = g[key] || []).push(r);
    }
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  const years = useMemo(() => {
    const y = new Set([currentYear - 1, currentYear, currentYear + 1, year]);
    return Array.from(y).sort();
  }, [currentYear, year]);

  return (
    <div className="space-y-4" data-testid="holidays-tab">
      <Card className="p-4 border-slate-200 rounded-md bg-white">
        <div className="flex items-center gap-3 flex-wrap">
          <CalendarDays className="h-5 w-5 text-brand" />
          <div className="font-heading font-semibold text-[15px] flex-1">Holiday Master · {year}</div>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
            <SelectTrigger className="w-[110px]" data-testid="holidays-year"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-[12.5px] px-2 py-1.5 rounded border border-slate-200 bg-slate-50">
            <Checkbox checked={autoSundayEnabled} onCheckedChange={toggleAutoSunday} disabled={!canEdit} data-testid="auto-sundays-toggle" />
            <span>Auto-mark Sundays as holidays in {year}</span>
          </label>
        </div>
        <div className="text-[11.5px] text-slate-500 mt-2">
          Uploaded holidays reduce "Working days" in the monthly report. Sunday auto-off is applied when computing the report — Sundays are <b>not</b> stored as rows here.
        </div>
      </Card>

      {canEdit && (
        <Card className="p-4 border-slate-200 rounded-md bg-white">
          <div className="grid grid-cols-1 sm:grid-cols-[160px_minmax(0,1fr)_auto] gap-2 items-end">
            <div>
              <label className="text-[10.5px] uppercase tracking-widest font-semibold text-slate-600">Date</label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="holiday-date" />
            </div>
            <div>
              <label className="text-[10.5px] uppercase tracking-widest font-semibold text-slate-600">{form.working_override ? "Working-day reason" : "Holiday name"}</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={form.working_override ? "e.g. Sunday shift for shipment" : "e.g. Republic Day"} data-testid="holiday-name" />
            </div>
            <div className="flex gap-2">
              <Button className="bg-brand text-white hover:bg-brand-dark" onClick={addOne} data-testid="holiday-add"><Plus className="h-4 w-4 mr-1.5" />Add</Button>
            </div>
          </div>

          <label className="mt-3 flex items-center gap-2 text-[12.5px] px-2 py-1.5 rounded border border-amber-200 bg-amber-50 w-fit cursor-pointer">
            <Checkbox
              checked={form.working_override}
              onCheckedChange={(v) => setForm({ ...form, working_override: !!v })}
              data-testid="holiday-working-override"
            />
            <span><b>Working-day override</b> — mark this date as a WORKING day (overrides auto-Sunday-off and any same-day holiday for the selected lines only). Attendance / output triggers WILL fire for those lines.</span>
          </label>

          {form.working_override && (
            <div className="mt-3 rounded border border-amber-200 bg-amber-50/60 p-3" data-testid="override-line-picker">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10.5px] uppercase tracking-widest font-semibold text-amber-800">Applies to lines *</span>
                <div className="flex gap-3 text-[11.5px]">
                  <button type="button" className="text-brand underline underline-offset-2" onClick={() => setForm({ ...form, line_ids: (lines || []).map((l) => l.id) })} data-testid="override-select-all-lines">Select all</button>
                  <button type="button" className="text-slate-500 underline underline-offset-2" onClick={() => setForm({ ...form, line_ids: [] })} data-testid="override-clear-lines">Clear</button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {(lines || []).map((l) => {
                  const on = form.line_ids.includes(l.id);
                  return (
                    <label key={l.id} className={`flex items-center gap-1.5 px-2.5 py-1 rounded border text-[12.5px] cursor-pointer ${on ? "border-brand bg-brand/10 text-brand font-semibold" : "border-slate-200 bg-white text-slate-600"}`} data-testid={`override-line-${l.id}`}>
                      <Checkbox
                        checked={on}
                        onCheckedChange={(v) => setForm({ ...form, line_ids: v ? [...form.line_ids, l.id] : form.line_ids.filter((x) => x !== l.id) })}
                      />
                      {l.name}
                    </label>
                  );
                })}
                {(!lines || lines.length === 0) && <span className="text-[12px] text-slate-500">No production lines found.</span>}
              </div>
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-2 items-center">
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleUpload} className="hidden" data-testid="holiday-file-input" />
            <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="holiday-bulk-upload">
              <Upload className="h-4 w-4 mr-1.5" />Bulk upload (Excel/CSV)
            </Button>
            <Button size="sm" variant="ghost" onClick={downloadTemplate}>
              <Download className="h-4 w-4 mr-1.5" />Template
            </Button>
            <span className="text-[11px] text-slate-400 ml-2">Columns: Date (YYYY-MM-DD or DD/MM/YYYY), Name</span>
          </div>
        </Card>
      )}

      {grouped.length === 0 ? (
        <Card className="p-8 border-slate-200 rounded-md bg-white text-center text-[13px] text-slate-500">
          No holidays uploaded for {year}. {canEdit ? "Add one above or bulk-upload an Excel." : "Ask an admin to upload the holiday list."}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {grouped.map(([month, list]) => {
            const label = new Date(month + "-01").toLocaleDateString("en-GB", { month: "long", year: "numeric" });
            return (
              <Card key={month} className="border-slate-200 rounded-md bg-white overflow-hidden">
                <div className="px-4 py-2 border-b border-slate-100 bg-slate-50">
                  <div className="font-heading font-semibold text-[13.5px] text-slate-700">{label}</div>
                  <div className="text-[11px] text-slate-500">{list.length} holiday{list.length === 1 ? "" : "s"}</div>
                </div>
                <div className="divide-y divide-slate-100">
                  {list.map((h) => (
                    <div key={h.id} className="px-4 py-2 flex items-center gap-3" data-testid={`holiday-row-${h.date}`}>
                      <div className="text-[11px] font-mono text-slate-500 w-20">{h.date} · <span className="text-[10px]">{isoWeekday(h.date)}</span></div>
                      <div className="text-[13px] flex-1 min-w-0 truncate flex items-center gap-2">
                        {h.working_override && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200 shrink-0" title="Working-day override — attendance/output triggers WILL fire on this date">WORKING</span>
                        )}
                        <span className="truncate">{h.name}</span>
                        {h.working_override && Array.isArray(h.applies_to) && h.applies_to.length > 0 && (
                          <span className="shrink-0 text-[10.5px] text-emerald-700 font-medium" data-testid={`override-lines-${h.date}`}>
                            → {h.applies_to.map(lineName).join(", ")}
                          </span>
                        )}
                      </div>
                      {canEdit && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 hover:bg-red-50" onClick={() => removeOne(h.id, h.date)} data-testid={`holiday-delete-${h.date}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
